import { FilterValue, SorterResult } from 'antd/es/table/interface';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBrandListQuery } from 'api';
import { TablePaginationConfig, TableProps } from 'components';
import { dateFormatter } from 'constants/index';
import { ADMIN_ROUTES } from 'constants/routes';
import { BrandDataType } from 'pages/admin/brands';
import { OrderType, PaginationType, SortParamsType } from 'typings';

export interface IFilterParams {
  name?: string;
  tags?: string[];
  countryId?: string;
  industryId?: string;
  createdDateFrom?: string;
  createdDateTo?: string;
  minPurchaseRequests?: number;
  maxPurchaseRequests?: number;
}

const initialPagination: PaginationType = { page: 1, size: 10 };
const initialSort: SortParamsType = {
  field: null,
  order: null,
};

export const useBrands = () => {
  const [addModalState, setAddModalState] = useState<boolean>(false);
  const [openFilters, setOpenFilters] = useState(false);
  const [filters, setFilters] = useState<IFilterParams>({});
  const [pagination, setPagination] = useState<PaginationType>(initialPagination);
  const [sort, setSort] = useState<SortParamsType>(initialSort);
  const [search, setSearch] = useState<string | undefined>(undefined);

  const navigate = useNavigate();
  const { data: { data: { content = [], totalElements = 0 } = {} } = {}, isLoading } = useBrandListQuery({
    size: pagination.size,
    page: pagination.page,
    sortField: sort.field,
    sortOrder: sort.order,
    search,
    filters,
  });
  const onOpenFilters = useCallback(() => setOpenFilters(true), []);
  const onCloseFilters = useCallback(() => setOpenFilters(false), []);

  const onAddModalStateChange = useCallback(() => {
    setAddModalState((prevState) => !prevState);
  }, []);
  const routeChange = useCallback(
    (value: string) => {
      navigate(`${ADMIN_ROUTES.brand.view}/${value}/management`);
      setAddModalState(false);
    },
    [navigate]
  );

  const handleTableChange: TableProps<BrandDataType>['onChange'] = useCallback(
    (
      pagination: TablePaginationConfig,
      _filters: Record<string, FilterValue | null>,
      sorter: SorterResult<BrandDataType> | SorterResult<BrandDataType>[]
    ) => {
      const sortField = Array.isArray(sorter) ? sorter[0].columnKey : sorter.columnKey;
      const sortOrder = Array.isArray(sorter) ? sorter[0].order : sorter.order;
      const order = sortOrder === 'ascend' ? 'asc' : sortOrder === 'descend' ? 'desc' : undefined;
      setPagination({
        page: pagination.current || 1,
        size: pagination.pageSize || 10,
      });
      setSort({
        field: sortField as string,
        order: order as OrderType,
      });
    },
    []
  );

  const onSearch = useCallback((value: string) => {
    setPagination((oState) => ({
      ...oState,
      page: 1,
    }));
    setSearch(value);
  }, []);

  const applyFilters = useCallback(
    (values: Record<string, any>) => {
      const _filters: any = {
        ...(values.brandId && { brandId: values.brandId }),
        ...(values.countryId && { countryId: values.countryId }),
        ...(values.industryId && { industryId: values.industryId }),
        ...(values.salesManagerId && { salesManagerId: values.salesManagerId }),
        ...(values.purchaseManagerId && { purchaseManagerId: values.purchaseManagerId }),
        ...(values.tags?.length && { tags: values.tags }),
        ...(values.minPurchaseRequests && { minPurchaseRequests: values.minPurchaseRequests }),
        ...(values.maxPurchaseRequests && { maxPurchaseRequests: values.maxPurchaseRequests }),
        ...(values.dateRange?.[0] && {
          createdDateFrom: dateFormatter(values.dateRange[0], 'start'),
        }),
        ...(values.dateRange?.[1] && {
          createdDateTo: dateFormatter(values.dateRange[1], 'end'),
        }),
        ...(values.lastUpdatedDate?.[0] && {
          lastUpdatedDateFrom: dateFormatter(values.lastUpdatedDate[0], 'start'),
        }),
        ...(values.lastUpdatedDate?.[1] && {
          lastUpdatedDateTo: dateFormatter(values.lastUpdatedDate[1], 'end'),
        }),
        ...(values.pricelistValidity?.[0] && {
          priceListValidityDateFrom: dateFormatter(values.pricelistValidity[0], 'start'),
        }),
        ...(values.pricelistValidity?.[1] && {
          priceListValidityDateTo: dateFormatter(values.pricelistValidity[1], 'end'),
        }),
      };
      setFilters(_filters);
      setPagination((oState) => ({
        ...oState,
        page: 1,
      }));
      onCloseFilters();
    },
    [onCloseFilters]
  );

  const resetFilters = useCallback(() => setFilters({}), []);

  return {
    onOpenFilters,
    onCloseFilters,
    onAddModalStateChange,
    addModalState,
    routeChange,
    data: content?.length ? content[0].brands : [],
    total: totalElements ?? 0,
    handleTableChange,
    openFilters,
    applyFilters,
    resetFilters,
    pagination,
    isLoading,
    onSearch,
  };
};
